import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import type { AppProps } from 'next/app';
import type { NextPage } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { DefaultSeo } from '@/components/DefaultSeo';
import { EarlyPreviewBannerProvider, useEarlyPreviewBanner } from '@/components/EarlyPreviewProvider';
import { Providers } from '@/components/Providers';
import { useViewportHeightVars } from '@/lib/hooks/useViewportHeightVars';
import '../globals.css';
import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';
import '@uppy/webcam/css/style.min.css';
import '@uppy/image-editor/css/style.min.css';
import 'leaflet/dist/leaflet.css';
import 'react-image-gallery/styles/image-gallery.css';
import { setGlobalOptimizePatterns } from '@/components/ImageLoader';
setGlobalOptimizePatterns(['sizzlenzfiles', 'assets.upstock.app']);
type NextPageWithLayout = NextPage & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
  /** When true, the page manages its own Header/Footer — _app renders only Providers + banner shell */
  noDefaultChrome?: boolean;
};
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};
export default function App(t0) {
  const $ = _c(31);
  const {
    Component,
    pageProps
  } = t0;
  useViewportHeightVars();
  const router = useRouter();
  const isMessagingPage = router.pathname === "/messaging";
  const [isEmbed, setIsEmbed] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const params = new URLSearchParams(window.location.search);
      const embed = params.has("embed");
      setIsEmbed(embed);
      if (embed) {
        document.body.classList.add("embed-ready");
      }
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  if (isEmbed) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Head><meta name="viewport" content="width=device-width, initial-scale=1, interactive-widget=resizes-content" /><meta name="format-detection" content="telephone=no, date=no, email=no, address=no" /></Head>;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <DefaultSeo />;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    let t5;
    if ($[4] !== Component || $[5] !== pageProps) {
      t5 = <>{t3}<Providers>{t4}<Component {...pageProps} /></Providers></>;
      $[4] = Component;
      $[5] = pageProps;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    return t5;
  }
  if (Component.getLayout) {
    if (Component.noDefaultChrome) {
      let t3;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = <Head><meta name="viewport" content="width=device-width, initial-scale=1, interactive-widget=resizes-content" /><meta name="format-detection" content="telephone=no, date=no, email=no, address=no" /><meta name="commission-factory-verification" content="538d1f39a5ec4a23885e061a8fbf30f1" /></Head>;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      let t4;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <DefaultSeo />;
        $[8] = t4;
      } else {
        t4 = $[8];
      }
      let t5;
      if ($[9] !== Component || $[10] !== pageProps) {
        t5 = Component.getLayout(<Component {...pageProps} />);
        $[9] = Component;
        $[10] = pageProps;
        $[11] = t5;
      } else {
        t5 = $[11];
      }
      let t6;
      if ($[12] !== t5) {
        t6 = <>{t3}<Providers>{t4}<EarlyPreviewBannerProvider>{t5}</EarlyPreviewBannerProvider></Providers></>;
        $[12] = t5;
        $[13] = t6;
      } else {
        t6 = $[13];
      }
      return t6;
    }
    let t3;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Head><meta name="viewport" content="width=device-width, initial-scale=1, interactive-widget=resizes-content" /><meta name="format-detection" content="telephone=no, date=no, email=no, address=no" /><meta name="commission-factory-verification" content="538d1f39a5ec4a23885e061a8fbf30f1" /></Head>;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    let t4;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <DefaultSeo />;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    let t5;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Header searchEnabled={true} />;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== Component || $[18] !== pageProps) {
      t6 = Component.getLayout(<Component {...pageProps} />);
      $[17] = Component;
      $[18] = pageProps;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t7;
    if ($[20] !== t6) {
      t7 = <>{t3}<Providers>{t4}<EarlyPreviewBannerProvider><CustomLayoutShell>{t5}<main className="flex-1 overflow-hidden">{t6}</main></CustomLayoutShell></EarlyPreviewBannerProvider></Providers></>;
      $[20] = t6;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    return t7;
  }
  let t3;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Head><meta name="viewport" content="width=device-width, initial-scale=1, interactive-widget=resizes-content" /><meta name="format-detection" content="telephone=no, date=no, email=no, address=no" /><meta name="commission-factory-verification" content="538d1f39a5ec4a23885e061a8fbf30f1" /></Head>;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  let t4;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <DefaultSeo />;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  const t5 = !isMessagingPage;
  let t6;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Header searchEnabled={true} />;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== Component || $[26] !== pageProps) {
    t7 = <Component {...pageProps} />;
    $[25] = Component;
    $[26] = pageProps;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== t5 || $[29] !== t7) {
    t8 = <>{t3}<Providers>{t4}<EarlyPreviewBannerProvider><DefaultLayoutShell showFooter={t5}>{t6}{t7}</DefaultLayoutShell></EarlyPreviewBannerProvider></Providers></>;
    $[28] = t5;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
}
function CustomLayoutShell(t0) {
  const $ = _c(5);
  const {
    children
  } = t0;
  const isBannerVisible = useEarlyPreviewBanner();
  const t1 = isBannerVisible ? 100 : 64;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      paddingTop: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== t2) {
    t3 = <div className="h-[100dvh] flex flex-col overflow-hidden" style={t2}>{children}</div>;
    $[2] = children;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function DefaultLayoutShell(t0) {
  const $ = _c(8);
  const {
    children,
    showFooter
  } = t0;
  const isBannerVisible = useEarlyPreviewBanner();
  const t1 = isBannerVisible ? 100 : 64;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      paddingTop: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== showFooter) {
    t3 = showFooter && <Footer />;
    $[2] = showFooter;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children || $[5] !== t2 || $[6] !== t3) {
    t4 = <div className="min-h-[100dvh] flex flex-col" style={t2}>{children}{t3}</div>;
    $[4] = children;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}